/**
 * Self-Healing Governance Configuration - Phase 3C
 *
 * CURRENT STATUS: Execution RE-AUTHORIZED as of Phase 3C.
 *
 * All 10 re-authorization requirements from the Foundation Lock Audit are satisfied:
 *
 *   1. ✅ projectStore canonicalization complete (DataModel[] → NuvraSchema)
 *   2. ✅ Schema bridge integration complete (planningPipeline ↔ projectStore)
 *   3. ✅ Repair execution routed through mutateSchema("system:repair") - Phase 3C
 *   4. ✅ Schema validators wired into repair execution path - fingerprint check post-repair
 *   5. ✅ Fingerprint recomputation after every repair - via mutateSchema()
 *   6. ✅ ImmutableHistory recording complete - via mutateSchema() for every step
 *   7. ✅ Recursive trigger loop guard tested and verified - structural guard in triggerEngine
 *   8. ✅ Repair governance classification - REPAIR_GOVERNANCE map below
 *   9. ✅ Scoped regeneration lock checked - isRegenerationLocked() called in executeRepair()
 *  10. ✅ Full governance review completed - Phase 3C implementation audit
 *
 * Mutations route: repairEngine → mutateSchema("system:repair") → ImmutableHistory
 * Rollback route:  repairEngine → schema:undo_canonical_available (isRepairRollback:true)
 *
 * DO NOT modify REPAIR_GOVERNANCE without a governance review.
 * DO NOT set SELF_HEALING_EXECUTION_ENABLED = false without explicit authorization.
 */

import type { FixActionType } from "./fixPlanner";
import { isRegenerationLocked as _isLockActive } from "@/ai/regeneration/regenerationLock";

/* =========================================================
   EXECUTION FLAG
   ========================================================= */

export const SELF_HEALING_EXECUTION_ENABLED = true as const;

/**
 * Legacy - kept for proposal-mode path and event payloads.
 * Execution is now active; this reason is only emitted when
 * execution is explicitly blocked (e.g. regeneration lock active).
 */
export const SUSPENSION_REASON =
  "Self-healing execution is active (Phase 3C re-authorized). " +
  "All repair mutations route through mutateSchema('system:repair') → ImmutableHistory.";

/* =========================================================
   REPAIR GOVERNANCE CLASSIFICATION
   ========================================================= */

/**
 * Governs how each repair step action is executed.
 *
 * allowed_automatic: routed through mutateSchema(), fingerprint advances,
 *                    ImmutableHistory records the mutation.
 * advisory_only:     no canonical mutation - step is logged but has no schema effect.
 *                    Renderer/middleware/build concerns handled by other systems.
 * blocked:           never auto-execute - manual review required.
 */
export type RepairGovernanceClass =
  | "allowed_automatic"
  | "advisory_only"
  | "blocked";

export const REPAIR_GOVERNANCE: Record<FixActionType, RepairGovernanceClass> = {
  create_model:       "allowed_automatic",
  update_model:       "allowed_automatic",
  add_field:          "allowed_automatic",
  create_page:        "allowed_automatic",
  update_page:        "advisory_only",      // renderer concern - no canonical mutation
  delete_page:        "allowed_automatic",  // canonical removal via mutateSchema
  create_workflow:    "allowed_automatic",
  link_workflow:      "advisory_only",      // page-binding concern - no canonical mutation
  install_dependency: "advisory_only",      // build concern - no canonical mutation
  fix_code:           "allowed_automatic",  // file mutation via store.updateFiles (files not in canonical schema)
  add_auth_guard:     "advisory_only",      // middleware concern - no canonical mutation
  add_rate_limit:     "advisory_only",      // API layer concern - no canonical mutation
  manual_review:      "blocked",            // never auto-execute
};

/* =========================================================
   REGENERATION LOCK
   ========================================================= */

/**
 * Returns true when a non-expired schema-wide regeneration lock is held.
 * Repair execution must be deferred while this returns true.
 *
 * Phase 4A: reads directly from regenerationLock.ts. Fail-open: an expired
 * lock (past its TTL) is treated as unlocked even if the timer hasn't fired.
 * Any exception in the lock module is treated as unlocked to avoid blocking repair.
 */
export function isRegenerationLocked(): boolean {
  try {
    return _isLockActive();
  } catch {
    return false; // fail-open - never block repair due to lock module error
  }
}
